/**
 * THE PICTURE CARRIES A CAPTION ONLY WHEN IT NAMES THE FACE. This is the one place that decides it.
 *
 * The field behind it is `card_assets.alt`, and the name is the whole story: it is ALT TEXT,
 * written for a screen reader, and it was being printed under the photograph as though it were
 * an editorial caption. The two are not the same object. A caption tells a reader something the
 * photograph cannot: who this is, where, when. Alt text describes the pixels, which is exactly
 * what a sighted reader does not need.
 *
 * Sampled from the live store 2026-09-04:
 *   "A man in a khaki short-sleeved shirt holds a microphone in one hand and raises his other
 *    hand while speaking outdoors near a wire fence."   ...under a photograph of Steve Irwin.
 *   "Person lying on grass, head tilted back, wearing denim shirt, eyes closed, relaxed pose"
 *   "Close-up profile of bearded man in black sleeveless shirt, blurred background"
 *
 * So nothing rendered. That was right about the prose and wrong about the whole field, and the
 * operator found the gap it left on 2026-09-08: a card headlined "Albanese government backs right
 * to opt out of social media algorithms" ran a photograph of a woman the headline never mentions.
 * The picture was correct, the headline was correct, and the reader had no way to connect them.
 *
 * THE NAME WAS IN THE FIELD THE WHOLE TIME. pipeline/images.py:person_caption() puts the person's
 * name in FRONT of the banked pixel description, on every rung, guarded by shows_one_person() so
 * a photograph of two players is never captioned with one of their names. The round log for that
 * card, written at 07:39, reads:
 *
 *   used the source article's own photo (Anika Wells. Woman in patterned blazer gesturing while
 *   speak) [named by the provider]
 *
 * The engine identified the face, wrote the name down, and no reader surface ever read it back.
 *
 * Measured across 1,200 live cards on 2026-09-08:
 *   177 (14.8%)  alt leads with a name clause, and this prints it
 *    43 of those  the headline does NOT carry that surname, which is the operator's card exactly
 *   389 (32.4%)  "File image: ..." stand-ins, which stay silent here
 *   634 (52.8%)  pixel prose, which stays silent here
 *
 * SO THE RULE IS THE LEADING CLAUSE, NOT THE TEXT. Print the name the engine put in front; never
 * print the description behind it. "Anika Wells. Woman in patterned blazer gesturing while
 * speaking at a podium" renders as "Anika Wells" and nothing else.
 *
 * The alt ATTRIBUTE is deliberately untouched everywhere this is used. It is not shown to a
 * sighted reader, it is what a screen reader announces, and there the full description is right.
 */

/**
 * A person's name as person_caption() writes it: one to four capitalised words, allowing the
 * apostrophes, hyphens and regnal numerals that real names carry (O'Connell, Kim Jong-un,
 * King Charles III, Margrethe II).
 */
const NAME = "[A-Z][\\p{L}'’-]*(?: [A-Z][\\p{L}'’-]*){0,3}";

/** "Anika Wells. Woman in patterned blazer..." -> the name, with the description left behind. */
const NAME_THEN_PROSE = new RegExp(`^(${NAME})\\.\\s+\\S`, "u");

/** person_caption() returns the bare name when it had no description to sit in front of. */
const NAME_ALONE = new RegExp(`^(${NAME})\\.?$`, "u");

/**
 * The caption to render under a photograph: the name of the person in it, or "".
 *
 * Every caller treats "" as "print no caption line at all", so no empty element reaches the DOM.
 */
export function displayCaption(alt: string | null | undefined): string {
  const text = String(alt ?? "").trim();
  if (!text) return "";
  // A STAND-IN IS NOT A PORTRAIT. The "File image:" prefix marks a picture that is not of this
  // event, so whatever follows it is not the story's subject and must not be captioned as though
  // it were. Whether the label itself should print to the reader is a separate question from
  // this one, and it is not answered here.
  if (/^file image\b/i.test(text)) return "";
  const m = NAME_THEN_PROSE.exec(text) ?? NAME_ALONE.exec(text);
  return m ? m[1] : "";
}
